<template>
  <div class="page-pages tier tier-two">
    <Tooltip
      content="Here is a list of pages on your website, you can add as many as you need"
    />

    <div class="site-pages">
      <Permissions
        :permissions="currentUser.permissions"
        :requireAll="{page: ['list']}"
      >
        <section v-if="hasPages">
          <table-data
            :rows="pages"
            @rowClick="changeSitePage"
            @reorderItems="reorderItems"
            :sortable="true"
            :headings="[{heading: '', key: 'edit'}]"
          >
            <div
              v-for="(page, index) in pages"
              :slot="'td-' + index + '-edit'"
              :key="index"
            >
              <div class="page-item">
                <div>
                  <Sprite :name="pageIcon(page)" />
                </div>
                <span>
                  {{ page.name }}
                </span>
              </div>
            </div>
          </table-data>

          <router-link
            :to="{
              name: 'add-page',
              params: {companyId: companyId, siteId: siteId}
            }"
            class="add-new-page"
          >
            Add new page
          </router-link>
        </section>

        <section class="tier-content" v-else>
          <div class="helper-text no-items">
            <p>It's looks like you don't have any pages yet!</p>
            <p>Fortunately, it's very easy to
              <router-link
                :to="{
                  name: 'add-page',
                  params: {companyId: companyId, siteId: siteId}
                }"
              >create one</router-link>.
            </p>
          </div>
        </section>
      </Permissions>

      <transition name="column-left">
        <router-view :totalPages="totalPages" @updatePage="updatePage" />
      </transition>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import page from '@/mixins/page'
import confirm from '@/mixins/confirm'

import {readCompanySiteData} from '@/lib/sites'
import {readCompanySitePagesData, updateCompanySitePageData} from '@/lib/pages'

import Table from '@/components/Table'
import Sprite from '@/components/Sprite'
import Permissions from '@/components/Permissions'
import Tooltip from '@/components/Tooltip'

import debounce from 'lodash.debounce'

export default {
  name: 'Pages',

  mixins: [page, confirm],

  props: {
    companyId: {
      type: String,
      required: true
    },

    siteId: {
      type: String,
      required: true
    }
  },

  components: {
    TableData: Table,
    Sprite,
    Permissions,
    Tooltip
  },

  data() {
    return {
      site: {},
      pages: [],
      sitePage: {}
    }
  },

  created() {
    this.reorderItems = debounce(
      (items) => {
        this.updatePageWeights(items)
      },
      333,
      {trailing: true}
    )
  },

  methods: {
    getData() {
      readCompanySiteData(this.companyId, this.siteId).then(({data}) => {
        this.site = data
      })

      readCompanySitePagesData(this.companyId, this.siteId, {
        page: 1,
        page_size: 250
      }).then(({data, page}) => {
        this.pages = data

        this.handleExternalPages()

        this.pageData = page
      })
    },

    updatePage() {
      this.$emit('updatePage')
    },

    changeSitePage(page) {
      if (page.external) {
        this.$router.push({
          name: 'outbound-link',
          params: {
            link: `https://${this.site.shopify_url}/admin`
          }
        })
      } else {
        this.$router.push({name: 'page', params: {pageId: page.id}})
        this.$emit('changeSitePage', page.path)
      }
    },

    handleExternalPages() {
      if (this.hasShopify) {
        this.pages.push({
          name: 'shop',
          path: 'external',
          external: true
        })
      }
    },

    updatePageWeights(items) {
      items.forEach((item, index) => {
        item.menu_weight = index

        updateCompanySitePageData(
          this.companyId,
          this.siteId,
          item.id,
          item
        ).then(({data}) => {
          this.$emit('updatePage')
        })
      })
    }
  },

  computed: {
    ...mapState(['currentUser']),

    title() {
      return this.site.name
    },

    hasPages() {
      return !!this.pages.length
    },

    hasShopify() {
      return !!this.site.shopify_url
    },

    totalPages() {
      return this.pageData ? this.pageData.total_entries : 0
    },

    pageIcon() {
      return (page) => {
        switch (page.path) {
          case '/':
            return 'home'
            break // eslint-disable-line no-unreachable
          case '/contact':
            return 'contact-details'
            break // eslint-disable-line no-unreachable
          case 'external':
            return 'shopify'
            break // eslint-disable-line no-unreachable
          default:
            return 'page'
        }
      }
    }
  },

  mounted() {
    this.getData()
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/base/theme'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.site-pages
  .table
    box-shadow: none

    &.has-shop
      tr
        text-transform: capitalize

        &:last-child
          .drag-handle
            display: none

.add-new-page
  padding: $gap-medium
  border-top: 1px solid $color-border-light
  width: 100%
  text-align: center
  display: block
  text-transform: uppercase

  @include themify($themes)
    color: themed('color-secondary')

.page-pages
  box-shadow: inset -30px 0px 30px 0px rgba(0, 0, 0, 0.05)

  .inner-scroll
    max-height: calc(100vh - 205px)

.page-item
  display: flex
  align-items: center

  .sprite
    width: 30px
    height: 30px
    margin-right: $gap-small

    @include themify($themes)
      fill: themed('color-secondary')
</style>
